/**
 * Artifacts Service
 *
 * Frontend service for generating resume bullets + LinkedIn post draft from a saved conversation.
 */

import { getAuth } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getEmulatorHost, isUsingEmulators } from './emulator';

export interface ConversationArtifacts {
  conversationId: string;
  resumeBullets: string[];
  linkedinPost: string;
  model?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface ArtifactsOptions {
  language?: string;
}

export async function generateArtifactsFromConversation(
  conversationId: string,
  options?: ArtifactsOptions
): Promise<ConversationArtifacts> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const forceRefresh = process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';
  const idToken = await user.getIdToken(forceRefresh);

  const functions = getFunctions();
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';
  const baseUrl =
    isUsingEmulators()
      ? `http://${getEmulatorHost()}:${process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001'}/${projectId}/${region}`
      : functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  try {
    const response = await fetch(`${baseUrl}/generateArtifactsFromConversation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ conversationId, language: options?.language }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.error || `HTTP error! status: ${response.status}`);
    }

    const artifact = data?.artifact;
    if (!artifact || !Array.isArray(artifact.resumeBullets) || typeof artifact.linkedinPost !== 'string') {
      throw new Error('Invalid artifacts response');
    }

    return {
      conversationId,
      resumeBullets: artifact.resumeBullets,
      linkedinPost: artifact.linkedinPost,
      model: artifact.model,
      usage: artifact.usage,
    };
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout - artifacts service not responding');
    }
    throw error;
  }
}
